// src/utils/apiHelpers.js
// Centralized API utilities with caching

// Simple in-memory cache
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Helper function to generate SHA-1 hash
export async function sha1(str) {
  const buffer = new TextEncoder().encode(str);
  const hashBuffer = await crypto.subtle.digest('SHA-1', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

// Generate API headers
export async function getApiHeaders() {
  const apiKey = process.env.REACT_APP_PODCAST_API_KEY;
  const apiSecret = process.env.REACT_APP_PODCAST_API_SECRET;

  if (!apiKey || !apiSecret) {
    throw new Error("API credentials not found. Please check your .env file and restart the server.");
  }

  const apiHeaderTime = Math.floor(Date.now() / 1000);
  const hashStr = apiKey + apiSecret + apiHeaderTime;
  const apiHash = await sha1(hashStr);

  return {
    'User-Agent': 'Listen2App/0.1 (yourname@example.com)',
    'X-Auth-Key': apiKey,
    'X-Auth-Date': String(apiHeaderTime),
    'Authorization': apiHash,
  };
}

// Cache wrapper for API calls
export async function cachedFetch(url, options = {}, cacheKey = null) {
  const key = cacheKey || url;
  const now = Date.now();

  // Check cache
  if (cache.has(key)) {
    const { data, timestamp } = cache.get(key);
    if (now - timestamp < CACHE_DURATION) {
      console.log(`Using cached data for: ${key}`);
      return data;
    } else {
      // Remove expired cache
      cache.delete(key);
    }
  }

  // Fetch fresh data
  const response = await fetch(url, options);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      `API request failed with status ${response.status}: ${errorData.description || response.statusText}`
    );
  }

  const data = await response.json();

  // Store in cache
  cache.set(key, { data, timestamp: now });

  return data;
}

// Clear cache (useful for logout or manual refresh)
export function clearCache() {
  cache.clear();
}

// Search podcasts with caching
export async function searchPodcasts(query, max = 8) {
  const headers = await getApiHeaders();
  const url = `https://api.podcastindex.org/api/1.0/search/byterm?q=${encodeURIComponent(query)}&max=${max}`;
  const cacheKey = `search:${query}:${max}`;

  const data = await cachedFetch(url, { headers }, cacheKey);
  return data.feeds?.slice(0, max) || [];
}

// Get podcast details with caching
export async function getPodcastDetails(podcastId) {
  const headers = await getApiHeaders();
  let url = `https://api.podcastindex.org/api/1.0/podcasts/byfeedid?id=${podcastId}`;
  let cacheKey = `podcast:${podcastId}`;

  let data;
  try {
    data = await cachedFetch(url, { headers }, cacheKey);
    if (!data || data.status === "false" || (!data.feed && !data.id)) {
      throw new Error("Not found by feedid");
    }
  } catch (err) {
    // Fallback to iTunes ID lookup
    url = `https://api.podcastindex.org/api/1.0/podcasts/byitunesid?id=${podcastId}`;
    cacheKey = `podcast_itunes:${podcastId}`;
    data = await cachedFetch(url, { headers }, cacheKey);
  }

  // Normalize the response
  if (data && data.feed && data.feed.id) {
    return data.feed;
  } else if (data && data.id && data.title && data.image && data.description) {
    return {
      id: data.id || String(podcastId),
      title: data.title,
      author: data.author,
      description: data.description,
      image: data.image || data.artwork || data.feedImage,
      artwork: data.artwork || data.image || data.feedImage,
      url: data.url || data.feedUrl,
      itunesId: data.itunesId || data.feedItunesId,
      language: data.language || data.feedLanguage,
    };
  } else {
    throw new Error("Podcast data from API is in an unexpected format or missing essential information.");
  }
}

// Get podcast episodes with caching and lazy loading
export async function getPodcastEpisodes(feedId, max = 10) {
  const headers = await getApiHeaders();
  let url = `https://api.podcastindex.org/api/1.0/episodes/byfeedid?id=${feedId}&max=${max}`;
  let cacheKey = `episodes:${feedId}:${max}`;

  let data;
  try {
    data = await cachedFetch(url, { headers }, cacheKey);
    if (!data || data.status === "false") {
      throw new Error("Not found by feedid");
    }
  } catch (err) {
    url = `https://api.podcastindex.org/api/1.0/episodes/byitunesid?id=${feedId}&max=${max}`;
    cacheKey = `episodes_itunes:${feedId}:${max}`;
    data = await cachedFetch(url, { headers }, cacheKey);
  }
  
  return data.items || [];
}

// Combined call for podcast details AND episodes (reduces API calls)
export async function getPodcastWithEpisodes(podcastId, maxEpisodes = 5) {
  try {
    // Fetch podcast details first
    const podcastInfo = await getPodcastDetails(podcastId);

    // Use the returned feed ID for episodes
    const feedId = podcastInfo.id || podcastId;
    const episodes = await getPodcastEpisodes(feedId, maxEpisodes);

    return { podcastInfo, episodes };
  } catch (error) {
    console.error("Error fetching podcast with episodes:", error);
    throw error;
  }
}
